import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useBatch, useReprocessBatch } from '../hooks/useBatches';
import { OrdersTable } from '../components/OrdersTable';
import { StatusBadge } from '../components/StatusBadge';
import { AiModelSelect } from '../components/AiModelSelect';
import { DEFAULT_AI_MODEL, type AiModelKey } from '../utils/aiModels';

export function BatchDetail() {
  const { date } = useParams<{ date: string }>();
  const queryClient = useQueryClient();
  const reprocess = useReprocessBatch();

  const [isPolling, setIsPolling] = useState(false);
  const [hasAiInProgress, setHasAiInProgress] = useState(false);
  const [aiModel, setAiModel] = useState<AiModelKey>(DEFAULT_AI_MODEL);
  const triggerTimeRef = useRef<string | null>(null);

  const { data, isLoading, error } = useBatch(date || '', isPolling || hasAiInProgress);

  // Detect when reprocessing completes: importedAt changes to a value after the trigger time
  useEffect(() => {
    if (!isPolling || !triggerTimeRef.current || !data?.batch) return;

    if (data.batch.importedAt > triggerTimeRef.current) {
      setIsPolling(false);
      triggerTimeRef.current = null;
      // Refresh related queries now that processing is done
      queryClient.invalidateQueries({ queryKey: ['batches'] });
    }
  }, [isPolling, data?.batch?.importedAt, queryClient]);

  // Safety timeout: stop polling after 3 minutes regardless
  useEffect(() => {
    if (!isPolling) return;
    const timeout = setTimeout(() => {
      setIsPolling(false);
      triggerTimeRef.current = null;
    }, 3 * 60 * 1000);
    return () => clearTimeout(timeout);
  }, [isPolling]);

  // Track AI analysis in progress across orders
  useEffect(() => {
    const aiInProgress = data?.orders?.some(
      (o) => o.aiStatus === 'started' || o.aiStatus === 'fetching-context' || o.aiStatus === 'analyzing'
    ) ?? false;
    setHasAiInProgress(aiInProgress);
  }, [data?.orders]);

  const handleReprocess = () => {
    if (!date) return;
    triggerTimeRef.current = new Date().toISOString();
    setIsPolling(true);
    reprocess.mutate({ date, aiModel });
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">
          Error loading batch: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <Link
          to="/"
          className="mt-4 inline-block text-blue-600 hover:text-blue-800"
        >
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const formattedDate = date
    ? new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  const progressPercent =
    data?.batch && data.batch.totalOrders > 0
      ? Math.round((data.batch.resolvedOrders / data.batch.totalOrders) * 100)
      : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/"
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{formattedDate}</h1>
          {data?.batch && (
            <p className="text-sm text-gray-500">
              Imported{' '}
              {new Date(data.batch.importedAt).toLocaleString('en-US', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {data?.batch && <StatusBadge status={data.batch.status} />}
          {data?.batch && (
            <>
              <AiModelSelect
                id="batch-reprocess-ai-model"
                value={aiModel}
                onChange={setAiModel}
                disabled={isPolling || reprocess.isPending}
              />
              <button
                onClick={handleReprocess}
                disabled={isPolling || reprocess.isPending}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  isPolling
                    ? 'bg-amber-50 text-amber-700 cursor-default'
                    : reprocess.isPending
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                }`}
              >
                <svg
                  className={`w-4 h-4 ${isPolling ? 'animate-spin' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                {isPolling ? 'Reprocessing...' : 'Reprocess'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Progress */}
      {data?.batch && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Progress</span>
            <span className="font-medium text-gray-900">
              {data.batch.resolvedOrders} / {data.batch.totalOrders} orders resolved
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                data.batch.status === 'complete'
                  ? 'bg-green-500'
                  : data.batch.status === 'partial'
                    ? 'bg-blue-500'
                    : 'bg-yellow-500'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="mt-2 text-sm text-gray-500 text-right">
            {progressPercent}% complete
          </p>
        </div>
      )}

      {/* Orders Table */}
      <OrdersTable orders={data?.orders || []} isLoading={isLoading} />
    </div>
  );
}
